/* Wording for the system admin's Insights view and user timelines.
   Pure, so the phrasing can be pinned (adminFormat.test.js). */

export const PLATFORM_LABELS = {
  web: 'Website',
  android: 'Android app',
  ios: 'iOS app',
  unknown: 'Not recorded',
};

// `plan_source` on a user row.
export const PLAN_SOURCE_LABELS = {
  apple: 'Apple',
  google: 'Google',
  stripe: 'Stripe',
  iap: 'Store',
};

// `store` as RevenueCat sends it on an event.
export const EVENT_STORE_LABELS = {
  APP_STORE: 'App Store',
  MAC_APP_STORE: 'Mac App Store',
  PLAY_STORE: 'Google Play',
  STRIPE: 'Stripe',
  PROMOTIONAL: 'Promotional',
};

// What became of an event (routes/subscriptions.py). 'applied' says nothing.
export const OUTCOME_LABELS = {
  not_applicable: 'No change',
  sandbox_ignored: 'Sandbox',
  unknown_user: 'Unknown user',
  irrelevant_entitlement: 'Other product',
};

const FIELD_LABELS = {
  plan_type: 'Plan',
  plan_source: 'Source',
  plan_expiry_date: 'Expiry',
};

const joinParts = (parts) => parts.filter(Boolean).join(' · ');

// 'INITIAL_PURCHASE' -> 'Initial purchase'
export const prettyEvent = (eventType) =>
  eventType
    ? eventType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
    : '';

export const formatWhen = (iso) =>
  iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// { apple: 1, google: 3 } -> 'Google 3 · Apple 1'
export const storeSplit = (byStore) =>
  joinParts(
    Object.entries(byStore || {})
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([source, n]) => `${PLAN_SOURCE_LABELS[source] || source} ${n}`),
  );

// One person's use of one kind of device: { label: 'Android', app: true, website: false }
export const describeDeviceUse = ({ label, app, website }) => {
  const via = app && website ? 'app & website' : app ? 'app' : website ? 'website' : '';
  return via ? `${label} · ${via}` : label;
};

// A device row's split: { app: 120, website: 300 } -> 'App 120 · Website 300'
export const appWebsiteSplit = ({ app, website }) =>
  joinParts([app > 0 && `App ${app}`, website > 0 && `Website ${website}`]);

const changeValue = (field, value) => {
  if (value === null || value === undefined || value === '') return '—';
  // Audit snapshots carry the datetime as text; the day is what matters.
  return field === 'plan_expiry_date' ? String(value).slice(0, 10) : String(value);
};

/* A timeline row as { title, detail } — one line saying what happened, one
   saying the particulars. */
export const describeTimelineItem = (item) => {
  switch (item.kind) {
    case 'login': {
      const where = PLATFORM_LABELS[item.platform];
      return {
        title: where ? `Signed in · ${where}` : 'Signed in',
        detail: joinParts([item.os, item.build && `build ${item.build}`]),
      };
    }
    case 'subscription':
      return {
        title: prettyEvent(item.event_type),
        detail: joinParts([
          EVENT_STORE_LABELS[item.store] || item.store,
          item.outcome !== 'applied' && (OUTCOME_LABELS[item.outcome] || item.outcome),
          item.expires_at && `expires ${new Date(item.expires_at).toLocaleDateString()}`,
        ]),
      };
    case 'plan_change':
      return {
        title: `Plan changed by ${item.actor || 'unknown'}`,
        detail: joinParts(
          Object.entries(item.changes || {}).map(([field, v]) =>
            `${FIELD_LABELS[field] || field} ${changeValue(field, v?.old)} → ${changeValue(field, v?.new)}`),
        ),
      };
    default:
      return { title: item.kind, detail: '' };
  }
};
